import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/axios';
import { clearLegacyAuthStorage } from '@/lib/clearLegacyAuthStorage';
import { publishIncomingCalls } from '@/lib/incomingCalls';

const AuthContext = createContext(null);

function readImpersonatingFlag() {
  try {
    return document.cookie.split(';').some((c) => c.trim().startsWith('mamv_impersonating='));
  } catch {
    return false;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    clearLegacyAuthStorage(); // purge pre-P3 JWTs from localStorage
    (async () => {
      try {
        const r = await api.get('/auth/me');
        if (cancelled) return;
        const u = { ...r.data, impersonating: !!r.data.impersonating || readImpersonatingFlag() };
        localStorage.setItem('user', JSON.stringify(u));
        publishIncomingCalls(u);
        setUser(u);
      } catch {
        if (cancelled) return;
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const beat = setInterval(() => {
      api.get('/auth/me').catch(() => {});
    }, 45000);
    return () => clearInterval(beat);
  }, [user?.id]);

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { user: userData, is_app_only } = response.data;
    clearLegacyAuthStorage();
    // Session is HttpOnly cookie (incl. app-only accounts for /m).
    // Do not persist access_token from the response body.
    if (is_app_only) {
      localStorage.removeItem('user');
      return { ...userData, is_app_only: true };
    }
    localStorage.setItem('user', JSON.stringify(userData));
    publishIncomingCalls(userData);
    setUser({ ...userData, impersonating: false });
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    clearLegacyAuthStorage();
    localStorage.removeItem('user');
    setUser(null);
  };

  const hasRole = (roleName) => {
    return user?.roles?.some(role => role.name === roleName);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasRole, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
